// src/components/auth/AuthContainer.jsx
// =============================================
// AuthContainer.jsx
// Modern split-screen authentication container with image slideshow area
// Maintains theme awareness and responsive design
// =============================================

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { HousePortal } from '@/App';
import { LoginForm } from './LoginForm';
import { SignupForm } from './SignupForm';
import { CatLogo } from '@/components/CatLogo';
import { authManager } from '@/managers/AuthManager';
import { useTheme } from '@/managers/ThemeProvider';
import { Moon, Sun, ArrowLeft } from 'lucide-react';
import { TermsAndPrivacy } from './TermsAndPrivacy';

export function AuthContainer({ initialMode = 'signin' }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [mode, setMode] = useState(initialMode);
    const [isAnimating, setIsAnimating] = useState(false);
    const { isLightTheme, toggleTheme, theme } = useTheme();
    const [showTerms, setShowTerms] = useState(false);
    const [activeTermsTab, setActiveTermsTab] = useState('terms');
    const [authState, setAuthState] = useState(() => authManager.getState());
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [currentSlide, setCurrentSlide] = useState(0);

    // Slideshow images - replace with your AI-generated images
    const slideshowImages = [
        'https://firebasestorage.googleapis.com/v0/b/shelbuladev.firebasestorage.app/o/marketing%2Fcattio.webp?alt=media&token=1adf5bc1-36c6-48ef-88ee-f67c5b414a3c',
        'https://firebasestorage.googleapis.com/v0/b/shelbuladev.firebasestorage.app/o/marketing%2Fcha.webp?alt=media&token=7ece75b3-3dd2-47ae-9d4c-1045bb8c6fe1',
        'https://firebasestorage.googleapis.com/v0/b/shelbuladev.firebasestorage.app/o/marketing%2Fzorp.webp?alt=media&token=4f1e0b0b-90d1-455d-8400-cb25df06ca47',
    ];

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const unsubscribe = authManager.subscribe(state => {
            setAuthState(state);
        });

        return () => unsubscribe();
    }, []);

    // Slideshow functionality
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide(prevSlide => 
                prevSlide === slideshowImages.length - 1 ? 0 : prevSlide + 1
            );
        }, 5000);
        
        return () => clearInterval(interval);
    }, [slideshowImages.length]);

    const handleModeChange = (newMode) => {
        if (authState.isAuthenticating) return;
        
        setIsAnimating(true);
        setTimeout(() => {
            setMode(newMode);
            navigate(newMode === 'signin' ? '/login' : '/signup', { replace: true });
            setTimeout(() => {
                setIsAnimating(false);
            }, 50);
        }, 200);
    };

    const handleBackToSite = () => {
        navigate('/');
    };

    if (!authState.isInitialized) {
        return (
            <div className="fixed inset-0 bg-background flex items-center justify-center">
                <div className="w-10 h-10 relative">
                    <div className="absolute inset-0 bg-gradient-primary rounded-full opacity-20 blur-md"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-t-primary border-primary/30 rounded-full animate-spin"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 flex flex-col md:flex-row bg-background z-[1000]">
            {/* Left Panel - Auth Form */}
            <div className="w-full md:w-[480px] h-full flex flex-col relative overflow-auto">
                {/* Background Effects */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNTkuNSA2MEgwVjBoNjBWNjBoLS41ek01OSAxSDFtMCAxaDU4TTEgM2g1OE0xIDRoNTgiIHN0cm9rZT0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjAzKSIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==')] opacity-30"></div>
                    <div className="absolute top-0 left-0 right-[30%] h-[30%] bg-primary opacity-[0.03] blur-[100px] rounded-full"></div>
                    <div className="absolute bottom-[20%] right-[10%] w-[40%] h-[30%] bg-primary-light opacity-[0.03] blur-[80px] rounded-full"></div>
                </div>
                
                {/* Back to Site Button */}
                <button
    onClick={handleBackToSite}
    className="absolute top-6 left-6 z-50 flex items-center space-x-2 
             text-on-surface-dim hover:text-on-surface transition-colors duration-200
             bg-surface/80 hover:bg-surface rounded-md px-3 py-1.5"
>
    <ArrowLeft className="w-4 h-4" />
    <span className="text-sm">Back to Site</span>
</button>
                
                {/* Form Content */}
                <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 py-16 relative z-10">
                    {/* Logo Header */}
                    <div className="mb-8 flex items-center">
                        <CatLogo size={36} animated={true} />
                    </div>
                    
                    {/* Title Section */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-on-surface mb-2">
                            {mode === 'signin' ? 'Welcome Back' : 'Join Shelbula'}
                        </h1>
                        <p className="text-on-surface-dim text-sm">
                            {mode === 'signin' 
                                ? 'Sign in to continue your AI chat experience' 
                                : 'Create an account and supercharge your AI workflow'}
                        </p>
                    </div>
                    
                    {/* Form Container */}
                    <div className={`transition-all duration-300 ease-in-out transform
                                   ${isAnimating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                        {mode === 'signin' ? (
                            <LoginForm 
                                onModeChange={() => handleModeChange('signup')}
                            />
                        ) : (
                            <SignupForm 
                                onModeChange={() => handleModeChange('signin')}
                            />
                        )}
                    </div>

                    {/* Mobile Notice - Only shown on small screens */}
                    {isMobile && (
                        <div className="mt-6 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                            <p className="text-xs text-primary-light text-center">
                                For the best experience, use Chrome Desktop.
                                Mobile functionality may be limited.
                            </p>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="mt-10 flex justify-between items-center">
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-md transition-colors duration-200 
                                     text-on-surface-dim hover:text-on-surface"
                            aria-label="Toggle theme"
                        >
                            {isLightTheme ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                        </button>
                        
                        <button
                            onClick={() => setShowTerms(true)}
                            className="text-xs text-on-surface-dim hover:text-on-surface transition-colors"
                        >
                            Privacy Policy & Terms
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Right Panel - Image Slideshow Area */}
            <div className="hidden md:block flex-1 relative overflow-hidden">
                {/* Slideshow Background */}
                <div className="absolute inset-0 bg-surface-dark">
                    {/* Decorative elements */}
                    <div className="absolute inset-0 z-10">
                        <div className="absolute top-[10%] right-[20%] w-[25rem] h-[25rem] rounded-full bg-gradient-to-r from-primary/20 to-primary-light/10 blur-[100px] opacity-60"></div>
                        <div className="absolute bottom-[20%] left-[15%] w-[20rem] h-[20rem] rounded-full bg-gradient-to-r from-primary-light/10 to-primary/20 blur-[90px] opacity-50"></div>
                    </div>
                    
                    {/* Image Slideshow */}
                    <div className="absolute inset-0">
                        {slideshowImages.map((image, index) => (
                            <div 
                                key={index}
                                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out 
                                            ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
                                style={{
                                    backgroundImage: `url(${image})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                            >
                                {/* Optional image placeholder if images fail to load */}
                                {!image.startsWith('http') && !image.startsWith('/') && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="text-center p-8 max-w-md">
                                            <div className="w-12 h-12 border-4 border-t-primary border-primary/30 rounded-full animate-spin mx-auto mb-6"></div>
                                            <h3 className="text-lg font-bold text-on-surface mb-2">Shelbula CDE</h3>
                                            <p className="text-on-surface-dim text-sm mb-4">
                                                Supercharge your coding workflow with AI-assisted development.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background-dark/80 to-transparent z-20"></div>
                    <div className="absolute inset-0 bg-[url('/patterns/noise.png')] opacity-[0.1] mix-blend-soft-light z-20"></div>
                    
                    {/* Slideshow indicators */}
                    <div className="absolute bottom-28 left-1/2 transform -translate-x-1/2 flex space-x-1.5 z-30">
                        {slideshowImages.map((_, index) => (
                            <button 
                                key={index}
                                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 
                                            ${index === currentSlide 
                                                ? 'bg-primary w-4' 
                                                : 'bg-white/30 hover:bg-white/50'}`}
                                onClick={() => setCurrentSlide(index)}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
                
                {/* Branding Footer */}
                <div className="absolute bottom-8 left-8 right-8 z-30">
                    <div className="bg-surface/80 backdrop-blur-sm rounded-xl p-5 border border-border-color">
                        <p className="text-on-surface-dim text-sm text-center">
<em>Did you know?</em><br/> 🐈 Shelbula powered services donate 33% of profits annually to emergency vet care for cats & dogs. 🐩</p>
                    </div>
                </div>
            </div>

            {/* Terms Modal */}
            {showTerms && (
                <HousePortal>
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2000] p-4">
                        <TermsAndPrivacy 
                            onClose={() => setShowTerms(false)}
                            initialTab={activeTermsTab}
                        />
                    </div>
                </HousePortal>
            )}
        </div>
    );
}

export default AuthContainer;